import React from "react";

/*
 * Live fleet tracking illustration (white background, on-brand).
 * Different vehicle types move along their own routes on a light street grid.
 */

const Truck = () => (
  <g>
    <rect x="-14" y="-7" width="20" height="14" rx="2.5" fill="#0E0E10" />
    <rect x="6" y="-5" width="8" height="12" rx="2" fill="#4C30FF" />
    <circle cx="-8" cy="8" r="2.4" fill="#0E0E10" />
    <circle cx="9" cy="8" r="2.4" fill="#0E0E10" />
  </g>
);

const Van = () => (
  <g>
    <rect x="-11" y="-6" width="17" height="12" rx="3" fill="#4C30FF" />
    <rect x="6" y="-4" width="5" height="10" rx="2" fill="#0E0E10" />
    <circle cx="-6" cy="7" r="2.2" fill="#0E0E10" />
    <circle cx="7" cy="7" r="2.2" fill="#0E0E10" />
  </g>
);

const Bike = () => (
  <g>
    <circle cx="-6" cy="4" r="4" fill="none" stroke="#0E0E10" strokeWidth="2" />
    <circle cx="6" cy="4" r="4" fill="none" stroke="#0E0E10" strokeWidth="2" />
    <path d="M-6 4 L-1 -4 L5 -4 L6 4 M-1 -4 L1 4" fill="none" stroke="#0E0E10" strokeWidth="2" strokeLinejoin="round" />
    <rect x="-3" y="-8" width="7" height="5" rx="1.5" fill="#4C30FF" />
  </g>
);

const Car = () => (
  <g>
    <rect x="-10" y="-5" width="20" height="10" rx="4" fill="#0E0E10" />
    <rect x="-5" y="-8" width="10" height="5" rx="2" fill="#4C30FF" />
    <circle cx="-6" cy="6" r="2.2" fill="#0E0E10" />
    <circle cx="6" cy="6" r="2.2" fill="#0E0E10" />
  </g>
);

const vehicles = [
  { id: "truck", Icon: Truck, dur: "16s", path: "M-20 210 H 500", label: "TRK-204" },
  { id: "van", Icon: Van, dur: "13s", path: "M150 420 V -20", label: "VAN-17", alert: "TEMP BREACH · 9.4°C" },
  { id: "bike", Icon: Bike, dur: "11s", path: "M-20 336 Q 240 300 500 340", label: "RDR-88" },
  { id: "car", Icon: Car, dur: "14s", path: "M340 -20 V 420", label: "CAR-09" },
];

const FleetMapIllustration = ({ className = "" }) => (
  <svg
    viewBox="0 0 480 400"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Live map showing trucks, vans, bikes and cars moving in real time"
    data-testid="fleet-map-illustration"
  >
    <defs>
      <pattern id="fm-grid" width="24" height="24" patternUnits="userSpaceOnUse">
        <path d="M24 0H0V24" fill="none" stroke="#EEEEE9" strokeWidth="1" />
      </pattern>
      <filter id="fm-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0E0E10" floodOpacity="0.10" />
      </filter>
    </defs>

    <rect width="480" height="400" fill="#FFFFFF" />
    <rect width="480" height="400" fill="url(#fm-grid)" />

    {/* streets */}
    <g stroke="#E6E6E0" strokeWidth="10" strokeLinecap="round" fill="none">
      <path d="M0 210 H480" />
      <path d="M150 0 V400" />
      <path d="M340 0 V400" />
      <path d="M0 110 Q 240 90 480 130" />
      <path d="M0 330 Q 240 300 480 340" />
    </g>
    <g stroke="#F1F1EC" strokeWidth="4" strokeLinecap="round" fill="none">
      <path d="M60 0 V400" />
      <path d="M250 0 V400" />
      <path d="M430 0 V400" />
      <path d="M0 50 H480" />
      <path d="M0 270 H480" />
    </g>

    {/* geofence zone */}
    <g>
      <rect x="196" y="140" width="190" height="130" rx="18" fill="#4C30FF" opacity="0.05" />
      <rect x="196" y="140" width="190" height="130" rx="18" fill="none" stroke="#4C30FF" strokeWidth="1.5" strokeDasharray="6 6" opacity="0.6" />
      <text x="210" y="160" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="9" fontWeight="600" fill="#3A1FE0" letterSpacing="1.2">
        ZONE · DOWNTOWN
      </text>
    </g>

    {/* depot */}
    <g transform="translate(62 318)" filter="url(#fm-shadow)">
      <rect x="-16" y="-16" width="32" height="32" rx="9" fill="#0E0E10" />
      <path d="M-7 3 V-3 L0 -8 L7 -3 V3 Z" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="-2.5" y="-1" width="5" height="4" fill="#FFFFFF" />
    </g>

    {/* static customer pins */}
    {[
      [410, 92],
      [96, 152],
      [268, 372],
    ].map(([x, y]) => (
      <g key={`${x}-${y}`} transform={`translate(${x} ${y})`} filter="url(#fm-shadow)">
        <path d="M0 8 C -7 0, -7 -6, 0 -12 C 7 -6, 7 0, 0 8 Z" fill="#FFFFFF" stroke="#4C30FF" strokeWidth="2" />
        <circle cy="-4" r="2" fill="#4C30FF" />
      </g>
    ))}

    {/* moving vehicles */}
    {vehicles.map((v) => (
      <g key={v.id} filter="url(#fm-shadow)">
        {/* pulse */}
        <circle r="14" fill={v.alert ? "#E5484D" : "#4C30FF"} opacity="0.12">
          <animateMotion dur={v.dur} repeatCount="indefinite" path={v.path} />
          <animate attributeName="r" values={v.alert ? "10;22;10" : "10;18;10"} dur={v.alert ? "1.1s" : "1.8s"} repeatCount="indefinite" />
          <animate attributeName="opacity" values={v.alert ? "0.35;0.05;0.35" : "0.18;0.04;0.18"} dur={v.alert ? "1.1s" : "1.8s"} repeatCount="indefinite" />
        </circle>
        {/* vehicle */}
        <g>
          <animateMotion dur={v.dur} repeatCount="indefinite" rotate="auto" path={v.path} />
          <v.Icon />
        </g>
        {/* label */}
        <g>
          <animateMotion dur={v.dur} repeatCount="indefinite" path={v.path} />
          <rect x="-22" y="-30" width="44" height="16" rx="8" fill={v.alert ? "#E5484D" : "#0E0E10"} />
          <text y="-19" textAnchor="middle" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="8.5" fontWeight="600" fill="#FFFFFF">
            {v.label}
          </text>
        </g>
        {/* alert card following the vehicle */}
        {v.alert && (
          <g>
            <animateMotion dur={v.dur} repeatCount="indefinite" path={v.path} />
            <rect x="18" y="-12" width="120" height="24" rx="12" fill="#FFFFFF" stroke="#E5484D" strokeWidth="1.5" />
            {/* thermometer */}
            <g transform="translate(30 0)">
              <rect x="-2" y="-7" width="4" height="10" rx="2" fill="none" stroke="#E5484D" strokeWidth="1.4" />
              <circle cy="5" r="2.6" fill="#E5484D" />
              <line x1="0" y1="-3" x2="0" y2="4" stroke="#E5484D" strokeWidth="1.4" />
            </g>
            <text x="42" y="3.5" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#E5484D">
              {v.alert}
            </text>
          </g>
        )}
      </g>
    ))}

    {/* live status card */}
    <g transform="translate(24 24)" filter="url(#fm-shadow)">
      <rect width="150" height="58" rx="12" fill="#FFFFFF" stroke="#E6E6E0" />
      <circle cx="18" cy="17" r="4" fill="#25D366">
        <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
      </circle>
      <text x="28" y="21" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="9" fontWeight="600" fill="#6B6B73" letterSpacing="1.2">
        LIVE · 4 VEHICLES
      </text>
      <text x="14" y="44" fontFamily="'Space Grotesk', Inter, sans-serif" fontSize="18" fontWeight="700" fill="#0E0E10">
        3 moving
      </text>
      <text x="96" y="44" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="12" fontWeight="600" fill="#E5484D">
        1 alert
      </text>
    </g>

    {/* alert chip */}
    <g transform="translate(262 24)" filter="url(#fm-shadow)">
      <rect width="194" height="30" rx="15" fill="#E5484D" />
      <circle cx="16" cy="15" r="4" fill="#FFFFFF">
        <animate attributeName="opacity" values="1;0.3;1" dur="1.1s" repeatCount="indefinite" />
      </circle>
      <text x="28" y="19" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontSize="11" fontWeight="600" fill="#FFFFFF">
        Temperature breach · VAN-17
      </text>
    </g>
  </svg>
);

export default FleetMapIllustration;
